import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * Captures the Android Chrome / Edge <c>beforeinstallprompt</c> event and
 * exposes a <c>promptInstall</c> trigger plus <c>canInstall</c> flag. iOS
 * Safari does NOT fire this event — on iOS we fall back to a static
 * "Add to Home Screen" tip in the UI rather than trying to prompt.
 */
export function useInstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const promptInstall = async () => {
    if (!evt) return null;
    await evt.prompt();
    const result = await evt.userChoice;
    setEvt(null);
    return result.outcome;
  };

  return {
    canInstall: !!evt,
    promptInstall
  };
}
